import { createContext, useContext } from "react";

import { StoreApi, createStore, useStore } from "zustand";

// type-only imports: programQueries imports this file at runtime, so value
// imports here would create an import cycle
import type {
  ItemProgramType,
  PlanningItemProgram,
} from "../data/planningItemProgram";
import type { ProgramSubmitErrors } from "../data/programQueries";

/**
 * Client state of one planning-tool session. The store is created per
 * CurrentProgramProvider (not global) so it resets when switching programs,
 * and is consumed through selectors so a change to one field only rerenders
 * its subscribers.
 */
export type PlanningToolState = {
  /**
   * Expansion counter per item-program id: > 0 means the row is open, and a
   * bump re-opens an already-open row (used to draw attention to errors).
   */
  openRowCounters: Record<string, number>;
  /** program-submit validation errors, keyed by item-program id */
  submitErrors: ProgramSubmitErrors;
  /**
   * Prefill for a "create new item/coupon/…" form, set when duplicating an
   * existing row (`{ type, ...formData }`). The matching section consumes it
   * and calls clearNewEntityDefaults.
   */
  newEntityDefaults: Record<string, any>;

  /** open one item-program's row, collapsing all others */
  expandItemProgram: (itemProgramId: string) => void;
  /** open the row of the item-program that belongs to the given item */
  expandItem: (itemId: string) => void;
  /** store submit errors and pop open the offending rows */
  setSubmitErrors: (errors: ProgramSubmitErrors) => void;
  clearSubmitErrors: () => void;
  setNewEntityDefaults: (defaults: {
    type: ItemProgramType | "offer";
    [field: string]: any;
  }) => void;
  clearNewEntityDefaults: () => void;
  /** current item-programs without subscribing to their changes */
  getItemPrograms: () => PlanningItemProgram[];
};

export const createPlanningToolStore = (
  getItemPrograms: () => PlanningItemProgram[]
) =>
  createStore<PlanningToolState>((set, get) => ({
    openRowCounters: {},
    submitErrors: {},
    newEntityDefaults: {},
    getItemPrograms,

    expandItemProgram: (itemProgramId) =>
      set((state) => ({
        // opening one row collapses all others
        openRowCounters: {
          [itemProgramId]: (state.openRowCounters[itemProgramId] ?? 0) + 1,
        },
      })),
    expandItem: (itemId) => {
      const itemProgram = getItemPrograms().find((ip) => ip.itemId === itemId);
      if (itemProgram) get().expandItemProgram(itemProgram.id);
    },
    setSubmitErrors: (errors) =>
      set((state) => ({
        submitErrors: errors,
        // pop open every row that has errors so they are visible
        openRowCounters: Object.keys(errors).reduce(
          (acc, id) => ({ ...acc, [id]: (acc[id] ?? 0) + 1 }),
          state.openRowCounters
        ),
      })),
    clearSubmitErrors: () => set({ submitErrors: {} }),
    setNewEntityDefaults: (defaults) => set({ newEntityDefaults: defaults }),
    clearNewEntityDefaults: () => set({ newEntityDefaults: {} }),
  }));

export const PlanningToolStoreContext =
  createContext<StoreApi<PlanningToolState> | null>(null);

/** Subscribe to a slice of the planning-tool session state. */
export const usePlanningTool = <T>(
  selector: (state: PlanningToolState) => T
): T => {
  const store = useContext(PlanningToolStoreContext);
  if (!store) {
    throw new Error(
      "usePlanningTool must be used within a CurrentProgramProvider"
    );
  }
  return useStore(store, selector);
};

/**
 * The store api (or null outside a CurrentProgramProvider), for imperative
 * access via getState() without subscribing.
 */
export const useOptionalPlanningToolStore = () =>
  useContext(PlanningToolStoreContext);
